// Stage 2 "Dashboard" · 2.3 ONE DASHBOARD — the single dashboard of the app (first item of the sidebar), fed by
// GET /api/firm-dashboard (backend/firm_dashboard.py, role-scoped: partner / manager / preparer). It replaces the engagement
// Dashboard (pages/Dashboard.jsx) and the Executive dashboard (pages/Executive.jsx); their URLs redirect here (App.js).
// Every figure comes from the API as computed there (one source each — Code Health Report Part C); the page only formats.
// SUNDAY BATCH · D-10: the figures are STORED per engagement and refreshed by the worker (after a change, nightly, Refresh now):
// the header "As of" is the OLDEST stored block shown (meta.as_of); stale / not-yet-calculated rows say "Updating…" and the page
// reads again while any is updating; Partner / Admin (the partner view) have "Refresh now".
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTimeFirm } from "@/lib/dt";
import "@/components/dashboard/firmDashboard.css";
import { FD } from "@/components/dashboard/fdTheme";
import FdTiles from "@/components/dashboard/FdTiles";
import FdPortfolio from "@/components/dashboard/FdPortfolio";
import FdBilling from "@/components/dashboard/FdBilling";
import FdStaffTime from "@/components/dashboard/FdStaffTime";
import FdHeatMap from "@/components/dashboard/FdHeatMap";
import FdMisstatements from "@/components/dashboard/FdMisstatements";
import FdMyTime from "@/components/dashboard/FdMyTime";
import { fill } from "@/components/dashboard/fdTheme";
import { asArray } from "@/lib/asArray";
import { RefreshCw } from "lucide-react";

export const UPDATING_POLL_MS = 8000;      // while a block is updating, read again (stops when none is, or after 15 reads)
const UPDATING_POLL_MAX = 15;

export default function FirmDashboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);   // the failed reply; worded at render time in the current language
  const [loading, setLoading] = useState(true);
  const [refreshMsg, setRefreshMsg] = useState("");
  const [refreshHover, setRefreshHover] = useState(false);
  const polls = useRef(0);

  const load = useCallback(() => {   // one read per visit (no dependency on the language, which changes once the user loads)
    setLoading(true); setErr(null);
    return api.get("/firm-dashboard")
      .then((r) => { setData(r.data || {}); })
      .catch((e) => { setErr(e || new Error("load failed")); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const updating = asArray(data && data.meta && data.meta.updating);
  const asOf = (data && data.meta && data.meta.as_of) || null;      // the oldest stored block shown — never the browser clock
  useEffect(() => {                  // D-10: an updating block is refreshed by the worker; read again until none is
    if (!updating.length || polls.current >= UPDATING_POLL_MAX) return undefined;
    const id = setTimeout(() => { polls.current += 1; load(); }, UPDATING_POLL_MS);
    return () => clearTimeout(id);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshNow = () => {
    setRefreshMsg("");
    api.post("/firm-dashboard/refresh")
      .then((r) => {      // STEP 2 · A5: a firm refresh already queued / running → nothing new queued, its job id returned
        setRefreshMsg(r && r.data && r.data.already_running ? "s2a3.refreshAlreadyRunning" : "sbC.refreshing");
        polls.current = 0; return load();
      })
      .catch(() => setRefreshMsg("sbC.refreshFailed"));
  };
  const errText = err ? (toErrMsg(err, t("fd.loadFailed")) || t("fd.loadFailed")) : "";

  const view = (data && data.view) || "preparer";
  const showBilling = view === "partner" && data && (Array.isArray(data.billing) || data.totals_by_currency || data.ai_firm_level);
  const showStaff = (view === "partner" || view === "manager") && data && Array.isArray(data.staff_time);

  return (
    <div className="fd-page -m-6 min-h-full space-y-4 p-6" data-testid="fd-page" dir={lang === "ar" ? "rtl" : "ltr"}>
      <header className="fd-header rounded-lg px-5 py-4 shadow" data-testid="fd-header">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t("fd.title")}</h1>
            <p className="text-sm opacity-90">{view === "preparer" ? t("fd.subtitleOwn") : t("fd.subtitleFirm")}</p>
          </div>
          <div className="text-xs opacity-90">
            <span className="rounded-full px-2 py-0.5" style={{ background: "rgba(255,255,255,0.16)" }} data-testid="fd-view">{t(`fd.view.${view}`)}</span>
            {asOf ? <span className="ms-2" data-testid="fd-as-of">{t("fd.asOf")} {fmtDateTimeFirm(asOf)}</span> : null}
            {updating.length ? <span className="ms-2" data-testid="fd-updating" aria-busy="true">{fill(t("sbC.updatingCount"), { n: updating.length })}</span> : null}
            {view === "partner" ? (
              <button type="button" onClick={refreshNow} title={t("sbC.refreshTitle")} data-testid="fd-refresh-now"
                      className="ms-2 inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium shadow-sm"
                      onMouseEnter={() => setRefreshHover(true)} onMouseLeave={() => setRefreshHover(false)}
                      style={{ background: refreshHover ? FD.thBg : FD.ivory, color: FD.emerald, fontSize: "13px", textDecoration: "none" }}>
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />{t("sbC.refreshNow")}
              </button>
            ) : null}
            {refreshMsg ? <span className="ms-2" data-testid="fd-refresh-msg">{t(refreshMsg)}</span> : null}
          </div>
        </div>
      </header>

      {err && (
        <div data-testid="fd-error" className="rounded-lg px-4 py-3 text-sm" style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" }}>
          {errText}
          <button type="button" onClick={load} className="ms-3 underline" data-testid="fd-retry">{t("fd.retry")}</button>
        </div>
      )}
      {loading && !data && !err && <div className="px-2 py-6 text-sm" style={{ color: FD.muted }} data-testid="fd-loading">{t("fd.loading")}</div>}

      {data && (
        <>
          <FdTiles tiles={data.tiles} t={t} lang={lang} />
          <FdPortfolio rows={data.portfolio} t={t} lang={lang} />
          {showBilling && <FdBilling rows={data.billing} totals={data.totals_by_currency} aiFirm={data.ai_firm_level} aiTotal={data.ai_firm_total} aiRate={data.ai_rate} t={t} lang={lang} />}
          {showStaff && <FdStaffTime rows={data.staff_time} engagements={data.portfolio} t={t} lang={lang} />}
          <FdHeatMap rows={data.heat_map} t={t} lang={lang} />
          <FdMisstatements rows={data.misstatements_vs_pm} t={t} lang={lang} />
          <FdMyTime rows={data.my_time_7d} t={t} lang={lang} />
        </>
      )}
    </div>
  );
}
